import { useRef } from 'react';
import type React from 'react';
import type { RefObject } from 'react';
import classNames from 'classnames';

import MessageItem from './message-item';
import MessageErrorItem from './message-error-item';

import { isEditableUserMessage } from '../../helpers/message-helpers';

import type { AiChatMessage } from '../../ai-chat.types';

interface ChatMessageListProps {
  messages: AiChatMessage[];
  oktaUserId: string;
  clientId: string | number;
  conversationId: string | null;
  isReadOnly?: boolean;
  isActionDisabled: boolean;
  isEditDisabled?: boolean;
  onRetry: (messageId: string, originalRequest?: string) => Promise<void>;
  onRegenerate: (messageId: string) => Promise<void>;
  onEditMessage?: (messageId: string) => void;
  messagesEndRef: RefObject<HTMLDivElement | null>;
  contentClassName?: string;
  className?: string;
}

const ChatMessageList = ({
  messages,
  oktaUserId,
  clientId,
  conversationId,
  isReadOnly,
  isActionDisabled,
  isEditDisabled = false,
  onRetry,
  onRegenerate,
  onEditMessage,
  messagesEndRef,
  contentClassName = 'max-w-[800px] mx-auto w-full',
  className,
}: ChatMessageListProps): React.JSX.Element => {
  const feedbackCloseRef = useRef<(() => void) | null>(null);

  return (
    <div
      className={classNames(
        'flex-1 min-h-0 overflow-x-hidden overflow-y-auto w-full scrollbar-thin',
        className
      )}
    >
      <div className={contentClassName}>
        {messages.map((message, index) =>
          message.error ? (
            <MessageErrorItem
              key={message.id}
              message={message}
              onRetry={() => void onRetry(message.id, message.originalRequest)}
              isLimit={message.limitError}
              isRetryDisabled={isActionDisabled}
            />
          ) : (
            <MessageItem
              key={`${message.id}-${Boolean(message.loading)}`}
              message={message}
              oktaUserId={oktaUserId}
              clientId={clientId}
              conversationId={conversationId ?? ''}
              feedbackCloseRef={feedbackCloseRef}
              onRegenerateMessage={(messageId) => void onRegenerate(messageId)}
              onEditMessage={
                isEditableUserMessage(messages, index) &&
                !isEditDisabled &&
                !isActionDisabled
                  ? onEditMessage
                  : undefined
              }
              isReadOnly={isReadOnly}
              isRegenerateDisabled={isActionDisabled}
            />
          )
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatMessageList;
